// ============================================================================
// UserAdmin.js — gestão de logins (superadmin): listar / criar / excluir / resetar.
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { apiListUsers, apiCreateUser, apiDeleteUser, apiResetPassword } from '../api';
import { useAuth } from './AuthContext';

const SAMSUNG = "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif";

export default function UserAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');

  const [form, setForm] = useState({ username: '', password: '', role: 'loja', nome: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiListUsers()
      .then((r) => setUsers(r.data.users || []))
      .catch((e) => setErro(e.response?.data?.detail || 'Erro ao carregar usuários'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const criar = async (e) => {
    e.preventDefault();
    setErro('');
    if (!form.username.trim() || !form.password) { setErro('Usuário e senha são obrigatórios'); return; }
    setSaving(true);
    try {
      await apiCreateUser({ ...form, username: form.username.trim() });
      setForm({ username: '', password: '', role: 'loja', nome: '' });
      flash('Login criado com sucesso');
      load();
    } catch (err) {
      setErro(err.response?.data?.detail || 'Erro ao criar login');
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (u) => {
    if (!window.confirm(`Excluir o login "${u.username}"?`)) return;
    setErro('');
    try { await apiDeleteUser(u.id); flash('Login excluído'); load(); }
    catch (err) { setErro(err.response?.data?.detail || 'Erro ao excluir'); }
  };

  const resetar = async (u) => {
    const nova = window.prompt(`Nova senha para "${u.username}":`);
    if (!nova) return;
    setErro('');
    try { await apiResetPassword(u.id, nova); flash(`Senha de "${u.username}" redefinida`); }
    catch (err) { setErro(err.response?.data?.detail || 'Erro ao redefinir senha'); }
  };

  const input = {
    padding: '10px 12px', borderRadius: 10, border: '1px solid #d9dde3', background: '#fff',
    fontSize: 14.5, fontFamily: SAMSUNG, color: '#0f172a', outline: 'none', boxSizing: 'border-box',
  };
  const roleChip = (role) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: role === 'superadmin' ? 'rgba(20,40,160,0.10)' : 'rgba(0,195,178,0.12)',
    color: role === 'superadmin' ? '#1428A0' : '#008577',
  });

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '8px 4px 40px', fontFamily: SAMSUNG }}>
      <h2 style={{ fontSize: 26, fontWeight: 700, color: '#1428A0', letterSpacing: '-0.02em', margin: '4px 0 4px' }}>Logins</h2>
      <p style={{ fontSize: 14, fontWeight: 400, color: '#64748b', margin: '0 0 22px' }}>
        Crie e gerencie os acessos. <b>Lojas</b> veem apenas o Sales Day 2.0 (sem upload). <b>Superadmin</b> tem acesso total.
      </p>

      {erro && <div style={{ marginBottom: 14, padding: '10px 13px', borderRadius: 10, background: 'rgba(255,67,55,0.08)', border: '1px solid rgba(255,67,55,0.22)', color: '#D63A2F', fontSize: 13.5, fontWeight: 600 }}>{erro}</div>}
      {msg && <div style={{ marginBottom: 14, padding: '10px 13px', borderRadius: 10, background: 'rgba(0,195,178,0.10)', border: '1px solid rgba(0,195,178,0.22)', color: '#008577', fontSize: 13.5, fontWeight: 600 }}>{msg}</div>}

      {/* Criar */}
      <form onSubmit={criar} style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 16, padding: 20, marginBottom: 22, boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Novo login</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr 0.9fr auto', gap: 10, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8' }}>Usuário</label>
            <input style={{ ...input, width: '100%', marginTop: 4 }} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="ex: loja-x" />
          </div>
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8' }}>Nome (exibição)</label>
            <input style={{ ...input, width: '100%', marginTop: 4 }} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="ex: Loja X" />
          </div>
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8' }}>Senha</label>
            <input style={{ ...input, width: '100%', marginTop: 4 }} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="senha" />
          </div>
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8' }}>Papel</label>
            <select style={{ ...input, width: '100%', marginTop: 4 }} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="loja">Loja</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>
          <button type="submit" disabled={saving} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: saving ? '#9fb0d8' : '#1428A0', color: '#fff', fontSize: 14.5, fontWeight: 700, fontFamily: SAMSUNG, cursor: saving ? 'default' : 'pointer', height: 42 }}>
            {saving ? '...' : 'Criar'}
          </button>
        </div>
      </form>

      {/* Lista */}
      <div style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>Carregando…</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f7f8fa' }}>
                <th style={{ textAlign: 'left', padding: '12px 18px', fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Usuário</th>
                <th style={{ textAlign: 'left', padding: '12px 18px', fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Nome</th>
                <th style={{ textAlign: 'left', padding: '12px 18px', fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Papel</th>
                <th style={{ textAlign: 'right', padding: '12px 18px', fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid #eef2f6' }}>
                  <td style={{ padding: '13px 18px', fontSize: 15, fontWeight: 700, color: '#1F2A44' }}>
                    {u.username}{u.username === user?.username && <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginLeft: 8 }}>(você)</span>}
                  </td>
                  <td style={{ padding: '13px 18px', fontSize: 14.5, color: '#475569' }}>{u.nome || '—'}</td>
                  <td style={{ padding: '13px 18px' }}><span style={roleChip(u.role)}>{u.role === 'superadmin' ? 'Superadmin' : 'Loja'}</span></td>
                  <td style={{ padding: '13px 18px', textAlign: 'right' }}>
                    <button onClick={() => resetar(u)} style={{ marginRight: 8, padding: '6px 12px', borderRadius: 8, border: '1px solid #d9dde3', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: SAMSUNG }}>Resetar senha</button>
                    <button onClick={() => excluir(u)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,67,55,0.30)', background: 'rgba(255,67,55,0.06)', color: '#D63A2F', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: SAMSUNG }}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
